/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  FiMapPin, FiPhoneCall, FiTool,
} from 'react-icons/fi';
import {
  Box, Typography, useTheme, Button,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import { trackEvent } from './Tracking';
import { zIndices } from '@/theme';
import Backdrop from '../atoms/Backdrop';
import Link from '../atoms/Link';
import ScrollToTopFab from '../molecules/ScrollToTopFab';
import WhatsappFab from '../molecules/WhatsappFab';

const BREAKPOINT = 'md';

export default function FloatingActionBar(props) {
  const { data } = props;
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: zIndices.floatingActionBar,
        pointerEvents: 'none',
        // Position: mobile
        bottom: 0,
        left: 0,
        width: '100%',
        height: 'auto',
        [theme.breakpoints.up(BREAKPOINT)]: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          // Position: desktop
          top: 0,
          right: 0,
          left: 'auto',
          bottom: 'auto',
          width: 'auto',
          height: '100%',
        },
      }}
    >
      <Backdrop
        sx={{
          display: {
            xs: 'flex',
            [BREAKPOINT]: 'none',
          },
          pointerEvents: 'all',
          flexDirection: 'row',
          justifyContent: 'center',
          px: 1,
          '& > *:not(:last-child)': {
            marginRight: theme.spacing(1),
          },
        }}
      >
        <Actions
          color="default"
          data={data}
          theme={theme}
        />
      </Backdrop>
      <Backdrop
        sx={{
          display: {
            xs: 'none',
            [BREAKPOINT]: 'flex',
          },
          pointerEvents: 'all',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: theme.spacing(1),
          '& > *:not(:last-child)': {
            marginRight: 0,
            marginBottom: theme.spacing(1),
          },
          backgroundColor: `${theme.palette.dark.main} !important`,
          borderTopLeftRadius: theme.shape.borderRadius,
          borderBottomLeftRadius: theme.shape.borderRadius,
          '& > *': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
        }}
      >
        <Actions
          color="secondary"
          data={data}
          isDesktop
          theme={theme}
        />
      </Backdrop>
    </Box>
  );
}

FloatingActionBar.propTypes = {
  data: PropTypes.object,
};

function Actions(props) {
  const {
    data,
    color,
    isDesktop,
    theme,
  } = props;
  const { t } = useTranslation();
  const {
    sidebarPhone,
    sidebarUrlLocations,
    sidebarUrlWorkshop,
    sidebarUrlCarsearch,
  } = data ?? {};

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {!isDesktop && (
        <span style={{ marginRight: 'auto' }}>
          <WhatsappFab
            iconButton
            phoneNumbers={data.whatsappNumbers}
          />
        </span>
      )}
      <Link href={`tel:${sidebarPhone}`} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.call')}
          color={color}
          component="a"
          onClick={() => {
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'Phone',
              origin: 'Floating Action Bar',
            });
          }}
          sx={!isDesktop ? {
            flexDirection: 'column',
            borderRadius: 'unset',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          } : {}}
        >
          <FiPhoneCall
            style={iconStyles}
          />
          {!isDesktop && (
            <Typography variant="caption">
              {t('components.organisms.FloatingActionBar.call')}
            </Typography>
          )}
        </Button>
      </Link>
      {isDesktop && (
        <Typography color="secondary" variant="caption">
          {t('components.organisms.FloatingActionBar.call')}
        </Typography>
      )}
      <Link href={sidebarUrlLocations ?? '#'} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.locations')}
          color={color}
          component="a"
          onClick={() => {
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'Branches',
              origin: 'Floating Action Bar',
            });
          }}
          sx={!isDesktop ? {
            flexDirection: 'column',
            borderRadius: 'unset',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          } : {}}
        >
          <FiMapPin
            style={iconStyles}
          />
          {!isDesktop && (
            <Typography variant="caption">
              {t('components.organisms.FloatingActionBar.locations')}
            </Typography>
          )}
        </Button>
      </Link>
      {isDesktop && (
        <Typography color="secondary" variant="caption">
          {t('components.organisms.FloatingActionBar.locations')}
        </Typography>
      )}
      <Link href={sidebarUrlWorkshop ?? '#'} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.workshop')}
          color={color}
          component="a"
          onClick={() => {
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'WorkshopAppointment',
              origin: 'Floating Action Bar',
            });
          }}
          sx={!isDesktop ? {
            flexDirection: 'column',
            borderRadius: 'unset',
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          } : {}}
        >
          <FiTool
            style={iconStyles}
          />
          {!isDesktop && (
            <Typography variant="caption">
              {t('components.organisms.FloatingActionBar.workshop')}
            </Typography>
          )}
        </Button>
      </Link>
      {isDesktop && (
        <Typography color="secondary" variant="caption">
          {t('components.organisms.FloatingActionBar.workshop')}
        </Typography>
      )}
      <Link href={sidebarUrlCarsearch ?? '#'} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.vehicles')}
          color={color}
          component="a"
          onClick={() => {
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'Vehicles',
              origin: 'Floating Action Bar',
            });
          }}
          sx={!isDesktop ? {
            flexDirection: 'column',
            borderRadius: 'unset',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          } : {}}
        >
          <IconCarSearch />
          {!isDesktop && (
            <Typography variant="caption">
              {t('components.organisms.FloatingActionBar.vehicles')}
            </Typography>
          )}
        </Button>
      </Link>
      {isDesktop && (
        <Typography color="secondary" variant="caption">
          {t('components.organisms.FloatingActionBar.vehicles')}
        </Typography>
      )}
      {!isDesktop && (
        <Box style={{ marginLeft: 'auto' }}>
          <ScrollToTopFab iconButton />
        </Box>
      )}
    </Box>
  );
}

Actions.propTypes = {
  color: PropTypes.string,
  isDesktop: PropTypes.bool,
  data: PropTypes.shape({
    sidebarPhone: PropTypes.string,
    sidebarUrlLocations: PropTypes.string,
    sidebarUrlWorkshop: PropTypes.string,
    sidebarIrlCarsearch: PropTypes.string,
  }),
};

const iconStyles = {
  display: 'block',
  width: '24px',
  height: '24px',
  marginTop: '8px',
  marginBottom: '0px',
};

export function IconCarSearch({ width, height }) {
  return (
    <svg
      fill="currentColor"
      height="100%"
      stroke="currentColor"
      strokeWidth={3}
      style={{
        fillRule: 'evenodd',
        clipRule: 'evenodd',
        strokeLinejoin: 'round',
        strokeMiterlimit: 2,
        width: width ?? 30,
        height: height ?? 30,
      }}
      version="1.1"
      viewBox="0 0 100 100"
      width="100%"
    >
      <g transform="matrix(1,0,0,1,0,-168.94)">
        <g transform="matrix(0.42065,0,0,0.42065,0.358444,103.865)">
          <path
            d="M5.95,354.76C5.62,354.76 5.29,354.7 4.97,354.57C2.46,353.57 1.78,353.31 3.23,308.57C3.48,300.74 3.72,293.33 3.82,287.63C3.9,282.7 7.3,278.59 10.58,274.61C12.5,272.28 14.34,270.05 15.36,267.87C4.32,268.88 2.62,267.58 2.03,267.13C0.14,265.68 -0.17,262.16 1.11,256.67L1.21,256.25L1.44,255.89C6.38,248.16 15.29,250.93 21.18,252.76C22.56,253.19 24.52,253.8 25.6,253.92C30.86,242.01 38.45,227.85 45.69,225.24C55.13,222.05 73.89,221.12 90.74,220.49L94.27,220.36L90.25,233.28L88.44,233.3C76.44,233.46 55.68,234.89 51.07,238.03C46.62,241.06 39.99,254.57 36.47,262.54L92.12,261.59L92.67,263.39C102.47,295.39 133.97,308.89 174.87,298.6L177.92,297.83L177.98,300.97C178.6,333.54 178.14,351.21 176.61,353.48L175.87,354.58L144.12,354.58L142.89,340.72L37.84,341.69L36.34,354.58L6.81,354.58C6.53,354.67 6.25,354.72 5.96,354.72L5.95,354.76ZM148.7,349.62L172.57,349.62C173.32,343.42 173.32,321.14 173.04,304.2C152.33,308.88 133.56,307.7 118.61,300.75C104.11,294.01 93.72,282.25 88.47,266.67L28.89,267.69L30.34,264.17C31.4,261.6 40.88,238.94 48.26,233.91C54.86,229.41 79.19,228.51 86.56,228.34L87.4,225.63C70.93,226.29 55.13,227.34 47.34,229.96C43.06,231.51 36.27,241.91 29.67,257.11L29.36,257.82L28.7,258.23C26.46,259.63 23.48,258.71 19.7,257.53C13.35,255.56 8.6,254.45 5.88,258.24C5.49,260.09 5.28,262.1 5.36,263.12C7.49,263.43 13.45,263.1 18.37,262.53L21.28,262.19L21.15,265.12C20.94,269.89 17.63,273.9 14.43,277.78C11.57,281.25 8.87,284.52 8.81,287.7C8.71,293.44 8.47,300.86 8.22,308.71C7.76,322.89 7.09,343.64 7.71,349.6L31.87,349.6L33.36,336.75L147.44,335.69L148.68,349.59L148.7,349.62ZM4.8,263.01L85.99,327.34C78.81,327.34 69.87,326.53 64.6,321.42C61.68,318.59 60.21,314.8 60.21,310.15L60.21,307.66L124.48,307.45L124.52,309.92C124.59,314.84 123.25,318.74 120.54,321.52C115.21,326.97 105.89,327.06 96.88,327.14C95.32,327.15 93.77,327.17 92.26,327.21L91.11,327.24C89.58,327.29 87.85,327.34 85.99,327.34L4.8,263.01ZM65.41,312.65C65.78,314.76 66.66,316.47 68.08,317.84C73.19,322.8 84.31,322.45 90.95,322.25L92.11,322.21C93.66,322.17 95.24,322.15 96.83,322.14C104.82,322.07 113.09,321.99 116.96,318.03C118.3,316.66 119.1,314.83 119.4,312.47L65.42,312.65L65.41,312.65ZM31.57,307.2C29.29,307.2 26.97,306.65 24.7,305.54C18.37,302.44 14.24,295.81 14.86,289.77C15.18,286.7 17.05,281.2 25.98,278.93C32.77,277.2 40.28,282.41 43.61,288.43C46.55,293.73 46.09,299.01 42.37,302.56C39.16,305.63 35.42,307.21 31.56,307.21L31.57,307.2ZM28.72,283.58C28.21,283.58 27.7,283.64 27.21,283.76C24.07,284.56 20.24,286.34 19.83,290.27C19.43,294.17 22.53,298.9 26.89,301.04C29.32,302.23 34.1,303.53 38.91,298.93C41.64,296.31 40.28,292.73 39.23,290.83C37.13,287.03 32.66,283.57 28.71,283.57L28.72,283.58Z"
            style={{ fillRule: 'nonzero', strokeWidth: 4 }}
          />
        </g>
        <g transform="matrix(0.42065,0,0,0.42065,0.358444,103.865)">
          <path
            d="M222.96,331.34C222.13,331.34 221.28,331.28 220.41,331.15C214.98,330.35 207.28,321.25 195.23,306.37C189.33,299.09 183.26,291.58 179.29,288.21C173.59,292.12 166.02,294.42 160.51,295.29C138.54,298.75 119.93,292.6 108.1,277.96C96.1,263.11 92.91,241.67 99.98,223.35C106.34,206.85 120.21,195.79 138.04,192.99L138.43,195.46L138.04,192.99C157.23,189.98 175.15,195.88 187.2,209.18C199.35,222.6 203.49,241.2 198.54,260.23C197.3,264.98 195.05,269.67 191.84,274.19C194.33,277.22 202.84,283.96 209.26,289.05C222.79,299.77 231.82,307.17 234.07,312C236.48,317.18 236.18,322.6 233.27,326.51C230.93,329.65 227.25,331.35 222.96,331.35L222.96,331.34ZM179.19,281.99L180.71,283.01C185.18,286 191.95,294.37 199.12,303.23C206.77,312.69 217.26,325.63 221.15,326.21C224.69,326.73 227.58,325.78 229.27,323.52C231.06,321.11 231.17,317.6 229.55,314.11C227.63,309.99 215.73,300.55 206.16,292.97C194.41,283.66 187.71,278.23 186.5,274.91L186.03,273.63L186.86,272.54C190.21,268.14 192.51,263.57 193.71,258.98C198.22,241.62 194.5,224.69 183.5,212.55C172.61,200.53 156.33,195.2 138.82,197.95C122.57,200.5 110.44,210.16 104.65,225.17C98.32,241.6 101.27,261.57 112,274.84C122.66,288.03 139.61,293.54 159.74,290.37C165.14,289.52 172.72,287.17 177.76,283.15L179.19,282.01L179.19,281.99ZM148.17,284.17C139.91,284.17 131.17,281.45 122.91,274.85C110.51,264.95 105.41,249.19 109.59,233.73C113.91,217.77 126.55,206.4 142.57,204.06C156.46,202.03 174.65,210.59 182.29,222.76C194.63,242.4 187.43,262.55 174.83,273.8C167.9,279.98 158.4,284.17 148.16,284.17L148.17,284.17ZM143.3,209.01C129.28,211.06 118.22,221.03 114.42,235.04C110.71,248.76 115.05,262.18 126.03,270.95C143.37,284.79 161.36,279.13 171.5,270.07C182.54,260.22 188.84,242.59 178.06,225.42C171.47,214.93 155.22,207.27 143.29,209.01L143.3,209.01Z"
            style={{ fillRule: 'nonzero', strokeWidth: 4 }}
          />
        </g>
      </g>
    </svg>
  );
}
